/* eslint-disable react/jsx-props-no-spreading, react/require-default-props */
import { FC, useEffect } from 'react'
import ReactGA from 'react-ga4'
import { useParams } from 'react-router-dom'
import { useRecommendedProductsActions, useAppSelector } from '../hooks'
import { TemplateActionNames } from '../consts/defaultConfigValues/defaultConstants'
import {
  useClearCart,
  useDeleteProductFromCart,
  usePlaceOrderFlow,
} from '../hooks/cart'
import { ITemplate, ITemplateActions } from '../interfaces/templates'
import Default from './Default'
import { IGenericFunction } from '../interfaces'
import getAnalyticsItems from '../utils/getAnalyticsItems'
import { getAnalyticsCartPayload } from '../utils/getAnalyticsPayload'
import {
  useCheckoutRuleAutoSettle,
  useCheckoutRulePreflight,
} from '../hooks/checkout'

/**
 * Cart screen.
 *
 * Thin, like every template here: delegate rendering to `Default`, and own
 * only the work that belongs to this screen. That is the GA4 / Meta pageview
 * below, the checkout-rule pre-flight (COM-950) and the one-option auto-settle
 * (COM-1048).
 *
 * ⚠️ **A storefront whose cart container declares no `type: 'cart'` never
 * mounts this.** `type` is optional on `IContainersBase`, and
 * `BaseContainer`'s switch falls through to `Default` without it — so that
 * storefront renders a working cart and runs no pre-flight, silently. The
 * same gap COM-819's redirect guard has; recorded rather than fixed here.
 */
const Cart: FC<ITemplate> = ({ ...props }) => {
  const { config } = useAppSelector((state) => state.defaultSlice)
  const cart = useAppSelector((state) => state.cartSlice)

  /*
   * Asks the rule engine what blocks this cart, and routes out of it.
   *
   * Mounted here rather than in `EventManager` — which every container mounts
   * — because the question is only actionable on the cart, and asking it on
   * every screen would be one request per navigation for an answer nothing
   * else reads.
   */
  useCheckoutRulePreflight()

  /*
   * Takes the only value a checkout rule offers, so the shopper never reaches
   * a picker with one choice on it.
   *
   * Mounted here as well as on the summary because the answer has to exist
   * before the footer CTA acts, and the cart is the first screen where that is
   * true. The two mounts are sequential rather than simultaneous — `<Routes>`
   * renders one matched route and `BaseContainer` picks one template — so
   * whichever screen the shopper reaches second finds the decision already
   * made in `checkoutSlice.autoSettle`. See `templates/Summary.tsx` for why
   * that ordering is load-bearing and not merely convenient.
   */
  useCheckoutRuleAutoSettle()

  useEffect(() => {
    const { pathname } = window.location
    const route = pathname.split('/').pop()
    if (route === 'cart') {
      const cartAnalyticsItems = getAnalyticsItems(cart)
      const analyticsPayload = getAnalyticsCartPayload(
        cart,
        cartAnalyticsItems,
        config,
      )
      ReactGA.event('view_cart', analyticsPayload)
      window.fbq('trackCustom', 'ViewCart', {
        ...analyticsPayload,
        currency: config?.options?.currencyPrefix,
      })
    }
  }, [])
  return <Default {...props} />
}

export default Cart
